import { ACTION_BUSINESS_CLICK_WALKIN_WAITLIST_SUBMIT } from 'utils/analytics/eventTypeConstantsAmplitude';
import {
  Alert,
  Button,
  DialogActions,
  DialogContent,
  DialogForm,
  DialogHeading,
  FormCheckbox,
  SubmitButton,
  Typography,
} from 'omakase';
import {
  FormPhoneCountryCodeField,
  FormPhoneField,
  FormSelectField,
  FormTextField,
  Grid,
} from 'ui';
import { Formik, FormikErrors } from 'formik';
import { SmsAlertStopped } from 'components/sms/SmsAlertStopped';
import { consumer, core } from '@tock/coretypes';
import { convertFullNameToFirstLast, getFullName } from '@tock/shared/utils/patron';
import { getOrDefaultCountryCodeStringFromIsoCountry } from '@tock/shared/utils/phoneUtils';
import { isSmsPhoneStatusTypeOptOutHard } from 'utils/smsUtils';
import { privacyPolicyLink, termsLink } from 'utils/legal/termsUtils';
import { requireFields } from '@tock/shared/utils/FormValidators';
import { useActions } from '@tock/shared/utils/connect';
import { useConsumerConversionAnalytics } from 'hooks/analytics/useConsumerConversionAnalytics';
import { useHasDirectBrandUpdateFlags } from 'hooks/useHasDirectBrandUpdateFlags';
import { useMountEffect } from '@tock/hooks';
import { useState } from 'react';
import { walkinWaitlistDialogRoot } from 'components/business/walkin-waitlist/walkinWaitlistCssDefs';
import WalkinWaitlistAlertStatus from 'components/business/walkin-waitlist/WalkinWaitlistAlertStatus';
import WalkinWaitlistBusinessHeader from 'components/business/walkin-waitlist/WalkinWaitlistBusinessHeader';
import _ from 'lodash';

export interface Props {
  business: consumer.IBusinessConfig;
  onClose(): void;
  patronProfile?: core.IPatron | null;
  showExpiredWarning?: boolean;
}

export interface WalkinWaitlistFormValues {
  name: string;
  phone: string;
  phoneCountryCode: string;
  partySize: number;
  shortNotes?: string;
  consentsToSms: boolean;
}

export const handleValidate = async (
  values: Partial<WalkinWaitlistFormValues>,
  hasTwilioDirectBrandUpdatesFlag: boolean
) => {
  const errors: FormikErrors<WalkinWaitlistFormValues> = requireFields(
    values,
    'name',
    'phone',
    'consentsToSms'
  );
  if (!values.consentsToSms) {
    errors.consentsToSms = hasTwilioDirectBrandUpdatesFlag
      ? 'Required to submit a walk-in waitlist request.'
      : 'Please agree to receive text messages to join the walk-in waitlist';
  }
  return errors;
};

const WalkInWaitlistSignUpForm: React.FunctionComponent<Props> = ({
  onClose,
  business,
  patronProfile,
  showExpiredWarning = false,
}) => {
  const MIN_GUESTS = 1;
  const MAX_GUESTS = 8;

  const {
    actions: { waitlist: waitlistActions },
  } = useActions();
  const sendConversionAnalytics = useConsumerConversionAnalytics();

  const hasTwilioDirectBrandUpdatesFlag = useHasDirectBrandUpdateFlags();
  const [phoneConsentStatus, setPhoneConsentStatus] = useState<
    core.SmsPhone.StatusType | undefined
  >(patronProfile?.smsPhoneConsentStatus ?? undefined);
  const phoneIsHardOptOut = isSmsPhoneStatusTypeOptOutHard(phoneConsentStatus);

  const [numPartiesOnWaitlist, setNumPartiesOnWaitlist] = useState<number | undefined>(undefined);
  useMountEffect(() => {
    let isMounted = true;
    waitlistActions
      .getWalkinWaitlistSize(business.id)
      .then((response: ProtoResponse<core.WalkinWaitlistStatus>) => {
        if (isMounted) {
          setNumPartiesOnWaitlist(response.payload.sizeWaitlist);
        }
      });
    return () => {
      isMounted = false;
    };
  });

  const initialCountryCodeValueBusiness = business.country
    ? getOrDefaultCountryCodeStringFromIsoCountry(business.country)
    : undefined;

  const initialValues: WalkinWaitlistFormValues = {
    name: getFullName(patronProfile), // patron optional, defaults to ''
    phone: patronProfile?.phone || '',
    phoneCountryCode: patronProfile?.phoneCountryCode
      ? patronProfile.phoneCountryCode
      : initialCountryCodeValueBusiness
        ? initialCountryCodeValueBusiness
        : '+1 US',
    partySize: 2,
    shortNotes: '',
    consentsToSms: !hasTwilioDirectBrandUpdatesFlag, // default to true if the flag is off
  };

  const onFormSubmit = async (formValues: WalkinWaitlistFormValues) => {
    const { firstName, lastName } = convertFullNameToFirstLast(formValues.name, { reverse: false });

    // Note that we do not need to send the consentsToSms value to the backend as you can
    // only join the waitlist if the value is true.
    await waitlistActions.addToWalkinWaitlist({
      firstName: firstName,
      lastName: lastName,
      phone: formValues.phone,
      phoneCountryCode: formValues.phoneCountryCode,
      partySize: formValues.partySize,
      shortNotes: formValues.shortNotes,
    });
    onClose();
  };

  return (
    <Formik<WalkinWaitlistFormValues>
      initialValues={initialValues}
      onSubmit={onFormSubmit}
      validate={(values) => {
        return handleValidate(values, hasTwilioDirectBrandUpdatesFlag);
      }}
    >
      {({ isSubmitting }) => (
        <DialogForm>
          <DialogHeading id="walkin-waitlist-dialog-title" onClose={onClose}>
            Walk-in waitlist
          </DialogHeading>
          {showExpiredWarning && (
            <Alert severity="warning">
              Your previous walk-in waitlist request has expired. You can join again below.
            </Alert>
          )}

          <DialogContent css={walkinWaitlistDialogRoot}>
            <WalkinWaitlistBusinessHeader business={business} />

            {!_.isNil(numPartiesOnWaitlist) && (
              <WalkinWaitlistAlertStatus
                onWaitlist={false}
                numPartiesAhead={numPartiesOnWaitlist}
              />
            )}

            <Typography variant="body02" component="p" id="walkin-waitlist-dialog-description">
              The walk-in waitlist allows you to secure a place in line without making a
              reservation. Please ensure you’re in the area and ready to be seated when notified.
              You’ll get a text when your table is ready.
            </Typography>

            <Grid container spacing={1}>
              <Grid item xs={12}>
                <FormTextField fullWidth id="name" name="name" label="Name" required />
              </Grid>
              <Grid item xs={12}>
                <FormPhoneCountryCodeField fullWidth />
              </Grid>
              <Grid item xs={12}>
                <FormPhoneField fullWidth required onValidatePhone={setPhoneConsentStatus} />
              </Grid>
              <Grid item xs={12}>
                <FormSelectField name="partySize" id="partySize" fullWidth label="Party size">
                  {_.range(MIN_GUESTS, MAX_GUESTS + 1).map((i) => (
                    <option key={i} value={i}>
                      {i} Guest{i > 1 && 's'}
                    </option>
                  ))}
                </FormSelectField>
              </Grid>
              {business.onlineWalkinWaitlistGuestNotesEnabled && (
                <Grid item xs={12}>
                  <FormTextField
                    fullWidth
                    id="shortNotes"
                    name="shortNotes"
                    label="Special requests or seating preferences"
                    multiline={true}
                    rows={3}
                  />
                </Grid>
              )}
              <Grid item xs={12}>
                {hasTwilioDirectBrandUpdatesFlag ? (
                  <>
                    {phoneIsHardOptOut ? (
                      <SmsAlertStopped severity="warning" />
                    ) : (
                      <>
                        <FormCheckbox
                          name="consentsToSms"
                          label="I agree to receive text confirmation and updates from Tock."
                          value="true"
                          weight="book"
                          size="small"
                          required
                        />
                        <br />
                        <br />
                        <Typography
                          variant="caption"
                          color="fgSubtle"
                          data-testid="consent-to-sms-terms"
                        >
                          By providing your mobile number, you agree to the Tock {termsLink()} and{' '}
                          {privacyPolicyLink()}, and your mobile number may be used to send you text
                          messages and call you, through the use of an auto dialer, artificial,
                          and/or prerecorded call or text, to service your Tock account including to
                          confirm and manage reservations. Message and data rates may apply. Message
                          frequency varies. You may opt out of receiving text messages at any time
                          by replying STOP. Text HELP for help.
                        </Typography>
                      </>
                    )}
                  </>
                ) : (
                  <FormCheckbox
                    id="consentsToSms"
                    name="consentsToSms"
                    label={`I agree to receive text messages from ${business.name} to get updates on my waitlist status`}
                    weight="book"
                    required
                  />
                )}
              </Grid>
            </Grid>
          </DialogContent>
          <DialogActions>
            <Button variant="basic" size="large" onClick={onClose}>
              Cancel
            </Button>
            <SubmitButton
              variant="primary"
              size="large"
              isSubmitting={isSubmitting}
              disabled={hasTwilioDirectBrandUpdatesFlag && phoneIsHardOptOut}
              onClick={() => {
                sendConversionAnalytics(ACTION_BUSINESS_CLICK_WALKIN_WAITLIST_SUBMIT);
              }}
            >
              Submit
            </SubmitButton>
          </DialogActions>
        </DialogForm>
      )}
    </Formik>
  );
};

export default WalkInWaitlistSignUpForm;
